"use client";

// src/config.ts
import { persistQueryClient } from "@tanstack/query-persist-client-core";
import { createSyncStoragePersister } from "@tanstack/query-sync-storage-persister";
import { QueryClient } from "@tanstack/vue-query";
import {
  createConfig as createCoreConfig,
  createStorage,
  noopStorage
} from "@wagmi/core";
function createConfig({
  queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        cacheTime: 1e3 * 60 * 60 * 24,
        // 24 hours
        networkMode: "offlineFirst",
        refetchOnWindowFocus: false,
        retry: 0
      },
      mutations: {
        networkMode: "offlineFirst"
      }
    }
  }),
  storage = createStorage({
    storage: typeof window !== "undefined" && window.localStorage ? window.localStorage : noopStorage
  }),
  persister = typeof window !== "undefined" ? createSyncStoragePersister({
    key: "cache",
    storage,
    // Serialization is handled in `storage`.
    serialize: (x) => x,
    // Deserialization is handled in `storage`.
    deserialize: (x) => x
  }) : void 0,
  ...args
}) {
  const config = createCoreConfig({
    ...args,
    storage
  });
  if (persister)
    persistQueryClient({
      queryClient,
      persister,
      dehydrateOptions: {
        shouldDehydrateQuery: (query) => query.cacheTime !== 0 && // Note: adding a `persist` flag to a query key will instruct the
        // persister whether or not to persist the response of the query.
        query.queryKey[0].persist !== false
      }
    });
  return Object.assign(config, { queryClient });
}

// src/plugin.ts
import { VueQueryPlugin } from "@tanstack/vue-query";
import { inject, isVue2, markRaw, shallowRef, triggerRef } from "vue-demi";
var USE_WAGMI_KEY = "USE_WAGMI";
var UseWagmiPlugin = {
  install: (app, config) => {
    app.use(VueQueryPlugin, {
      queryClient: config.queryClient
    });
    const _config = shallowRef(markRaw(config));
    const unsubscribe = config.subscribe(() => {
      triggerRef(markRaw(_config));
    });
    if (app.onUnmount) {
      app.onUnmount(unsubscribe);
    } else {
      const originalUnmount = app.unmount;
      app.unmount = function vueQueryUnmount() {
        unsubscribe();
        originalUnmount();
      };
    }
    if (isVue2) {
      app.mixin({
        beforeCreate() {
          if (!this._provided) {
            const provideCache = {};
            Object.defineProperty(this, "_provided", {
              get: () => provideCache,
              set: (v) => Object.assign(provideCache, v)
            });
          }
          this._provided[USE_WAGMI_KEY] = _config;
        }
      });
    } else {
      app.provide(USE_WAGMI_KEY, _config);
    }
  }
};
function useConfig() {
  const config = inject(USE_WAGMI_KEY);
  if (!config?.value)
    throw new Error(
      [
        "`useConfig` must be used within `UseWagmiPlugin`.\n",
        "Read more: https://github.com/unicape/use-wagmi"
      ].join("\n")
    );
  return config;
}

// src/hooks/accounts/useAccount.ts
import { getAccount, watchAccount } from "@wagmi/core";
import {
  getCurrentScope,
  onScopeDispose,
  ref,
  reactive,
  toRefs,
  watchEffect
} from "vue-demi";

// src/utils.ts
import { isRef, unref } from "vue-demi";
function updateState(state, update) {
  Object.keys(state).forEach((key) => {
    state[key] = update[key];
  });
}
function cloneDeep(value, customizer) {
  if (customizer) {
    const result = customizer(value);
    if (result !== void 0 || isRef(value)) {
      return result;
    }
  }
  if (Array.isArray(value)) {
    return value.map((val) => cloneDeep(val, customizer));
  }
  if (typeof value === "object" && isPlainObject(value)) {
    const entries = Object.entries(value).map(([key, val]) => [
      key,
      cloneDeep(val, customizer)
    ]);
    return Object.fromEntries(entries);
  }
  return value;
}
function cloneDeepUnref(obj) {
  return cloneDeep(obj, (val) => {
    if (isRef(val)) {
      return cloneDeepUnref(unref(val));
    }
  });
}
function isPlainObject(value) {
  if (Object.prototype.toString.call(value) !== "[object Object]") {
    return false;
  }
  const prototype = Object.getPrototypeOf(value);
  return prototype === null || prototype === Object.prototype;
}

// src/hooks/accounts/useAccount.ts
function useAccount({ onConnect, onDisconnect } = {}) {
  const account = reactive(getAccount());
  const previousAccount = ref();
  const unwatch = watchAccount((data) => updateState(account, data));
  if (getCurrentScope())
    onScopeDispose(() => unwatch());
  watchEffect(() => {
    if (previousAccount.value?.status !== "connected" && account.status === "connected") {
      onConnect?.({
        address: account.address,
        connector: account.connector,
        isReconnected: previousAccount.value?.status === "reconnecting" || // if `previousAccount.status` is `undefined`, the connector connected immediately.
        previousAccount.value?.status === void 0
      });
    }
    if (previousAccount.value?.status === "connected" && account.status === "disconnected") {
      onDisconnect?.();
    }
    previousAccount.value = Object.assign({}, account);
  });
  return toRefs(account);
}

// src/hooks/accounts/useBalance.ts
import { fetchBalance } from "@wagmi/core";
import { computed as computed5, unref as unref8 } from "vue-demi";

// src/hooks/utils/useChainId.ts
import { computed } from "vue-demi";

// src/hooks/viem/usePublicClient.ts
import { getPublicClient, watchPublicClient } from "@wagmi/core";
import { readonly, shallowRef as shallowRef2, unref as unref2, watchEffect as watchEffect2 } from "vue-demi";
function usePublicClient({
  chainId
} = {}) {
  const publicClient = shallowRef2(
    getPublicClient({ chainId: unref2(chainId) })
  );
  watchEffect2((onCleanup) => {
    const unwatch = watchPublicClient(
      { chainId: unref2(chainId) },
      (client) => {
        publicClient.value = client;
      }
    );
    onCleanup(() => unwatch());
  });
  return readonly(publicClient);
}

// src/hooks/viem/useWalletClient.ts
import { getWalletClient, watchWalletClient } from "@wagmi/core";
import { unref as unref3, watchEffect as watchEffect3 } from "vue-demi";
function queryKey({ chainId }) {
  return [{ entity: "walletClient", chainId, persist: false }];
}
function queryFn({
  queryKey: [{ chainId }]
}) {
  return getWalletClient({ chainId });
}
function useWalletClient({
  chainId: chainId_,
  suspense,
  onError,
  onSettled,
  onSuccess
} = {}) {
  const { connector } = useAccount();
  const chainId = useChainId({ chainId: chainId_ });
  const walletClientQuery = useQuery(queryKey({ chainId }), queryFn, {
    cacheTime: 0,
    enabled: Boolean(connector),
    staleTime: Infinity,
    suspense,
    onError,
    onSettled,
    onSuccess
  });
  const queryClient = useQueryClient();
  watchEffect3((onCleanup) => {
    const unwatch = watchWalletClient(
      { chainId: unref3(chainId) },
      (walletClient) => {
        if (walletClient)
          queryClient.invalidateQueries(queryKey({ chainId }));
        else
          queryClient.removeQueries(queryKey({ chainId }));
      }
    );
    onCleanup(() => unwatch());
  });
  return walletClientQuery;
}

// src/hooks/viem/useWebSocketPublicClient.ts
import {
  getWebSocketPublicClient,
  watchWebSocketPublicClient
} from "@wagmi/core";
import { readonly as readonly2, shallowRef as shallowRef3, unref as unref4, watchEffect as watchEffect4 } from "vue-demi";
function useWebSocketPublicClient({ chainId } = {}) {
  const webSocketPublicClient = shallowRef3(
    getWebSocketPublicClient({
      chainId: unref4(chainId)
    })
  );
  watchEffect4((onCleanup) => {
    const unwatch = watchWebSocketPublicClient(
      { chainId: unref4(chainId) },
      (client) => {
        webSocketPublicClient.value = client;
      }
    );
    onCleanup(() => unwatch());
  });
  return readonly2(webSocketPublicClient);
}

// src/hooks/utils/useChainId.ts
function useChainId({ chainId } = {}) {
  const provider = usePublicClient({ chainId });
  return computed(() => provider.value.chain.id);
}

// src/hooks/utils/useInfiniteQuery.ts
import { useInfiniteQuery as useBaseQuery } from "@tanstack/vue-query";
import { computed as computed2 } from "vue-demi";

// src/hooks/utils/useQueryClient.ts
var useQueryClient = () => useConfig().value.queryClient;

// src/hooks/utils/useInfiniteQuery.ts
function useInfiniteQuery(queryKey17, queryFn17, options) {
  const queryClient = useQueryClient();
  const result = useBaseQuery({
    queryKey: queryKey17,
    queryFn: queryFn17,
    ...options,
    queryClient
  });
  const status = computed2(
    () => result.status.value === "loading" && result.fetchStatus.value === "idle" ? "idle" : result.status.value
  );
  const isIdle = computed2(() => status.value === "idle");
  const isLoading = computed2(
    () => status.value === "loading" && result.fetchStatus.value === "fetching"
  );
  return {
    ...result,
    status,
    isIdle,
    isLoading
  };
}

// src/hooks/utils/useInvalidateOnBlock.ts
import { unref as unref7 } from "vue-demi";

// src/hooks/network-status/useBlockNumber.ts
import { fetchBlockNumber } from "@wagmi/core";
import { unref as unref5, watchEffect as watchEffect5 } from "vue-demi";
function queryKey2({ chainId, scopeKey }) {
  return [{ entity: "blockNumber", chainId, scopeKey }];
}
function queryFn2({
  queryKey: [{ chainId }]
}) {
  return fetchBlockNumber({ chainId });
}
function useBlockNumber({
  cacheTime = 0,
  chainId: chainId_,
  enabled = true,
  scopeKey,
  staleTime,
  suspense,
  watch = false,
  onBlock,
  onError,
  onSettled,
  onSuccess
} = {}) {
  const chainId = useChainId({ chainId: chainId_ });
  const publicClient = usePublicClient({ chainId });
  const webSocketPublicClient = useWebSocketPublicClient({ chainId });
  const queryClient = useQueryClient();
  watchEffect5((onCleanup) => {
    if (!unref5(enabled))
      return;
    if (!unref5(watch) && !onBlock)
      return;
    const publicClient_ = webSocketPublicClient.value ?? publicClient.value;
    const unwatch = publicClient_.watchBlockNumber({
      onBlockNumber: (blockNumber) => {
        if (watch)
          queryClient.setQueryData(queryKey2({ chainId, scopeKey }), blockNumber);
        if (onBlock)
          onBlock(blockNumber);
      },
      emitOnBegin: true
    });
    onCleanup(() => unwatch());
  });
  return useQuery(
    queryKey2({
      scopeKey,
      chainId
    }),
    queryFn2,
    {
      cacheTime,
      enabled,
      staleTime,
      suspense,
      onError,
      onSettled,
      onSuccess
    }
  );
}

// src/hooks/network-status/useFeeData.ts
import { fetchFeeData } from "@wagmi/core";
import { computed as computed3, unref as unref6 } from "vue-demi";
function queryKey3({
  chainId,
  formatUnits,
  scopeKey
}) {
  return [{ entity: "feeData", chainId, formatUnits, scopeKey }];
}
function queryFn3({
  queryKey: [{ chainId, formatUnits }]
}) {
  return fetchFeeData({ chainId, formatUnits });
}
function useFeeData({
  cacheTime,
  chainId: chainId_,
  enabled = true,
  formatUnits = "wei",
  scopeKey,
  staleTime,
  suspense,
  watch,
  onError,
  onSettled,
  onSuccess
} = {}) {
  const chainId = useChainId({ chainId: chainId_ });
  const queryKey_ = computed3(
    () => queryKey3({
      chainId,
      formatUnits,
      scopeKey
    })
  );
  const feeDataQuery = useQuery(queryKey_, queryFn3, {
    cacheTime,
    enabled,
    staleTime,
    suspense,
    onError,
    onSettled,
    onSuccess
  });
  useInvalidateOnBlock({
    chainId,
    enabled: computed3(() => !!(unref6(enabled) && unref6(watch))),
    queryKey: queryKey_
  });
  return feeDataQuery;
}

// src/hooks/utils/useInvalidateOnBlock.ts
function useInvalidateOnBlock({
  chainId,
  enabled,
  queryKey: queryKey17
}) {
  const queryClient = useQueryClient();
  useBlockNumber({
    chainId,
    enabled,
    onBlock: () => unref7(enabled) && queryClient.invalidateQueries({ queryKey: unref7(queryKey17) }, { cancelRefetch: false }),
    scopeKey: unref7(enabled) ? void 0 : "idle"
  });
}

// src/hooks/utils/useQuery.ts
import { useQuery as useBaseQuery2 } from "@tanstack/vue-query";
import { computed as computed4 } from "vue-demi";
function useQuery(queryKey17, queryFn17, options) {
  const queryClient = useQueryClient();
  const result = useBaseQuery2({
    queryKey: queryKey17,
    queryFn: queryFn17,
    ...options,
    queryClient,
    queryKeyHashFn(queryKey18) {
      return JSON.stringify(
        queryKey18,
        (_, val) => typeof val === "bigint" ? val.toString() : isPlainObject(val) ? Object.keys(val).sort().reduce((result2, key) => {
          result2[key] = val[key];
          return result2;
        }, {}) : val
      );
    }
  });
  const status = computed4(
    () => result.status.value === "loading" && result.fetchStatus.value === "idle" ? "idle" : result.status.value
  );
  const isIdle = computed4(() => status.value === "idle");
  const isLoading = computed4(
    () => status.value === "loading" && result.fetchStatus.value === "fetching"
  );
  return {
    ...result,
    status,
    isIdle,
    isLoading
  };
}

// src/hooks/accounts/useBalance.ts
function queryKey4({
  address,
  chainId,
  formatUnits,
  scopeKey,
  token
}) {
  return [
    {
      entity: "balance",
      address,
      chainId,
      formatUnits,
      scopeKey,
      token
    }
  ];
}
function queryFn4({
  queryKey: [{ address, chainId, formatUnits, token }]
}) {
  if (!address)
    throw new Error("address is required");
  return fetchBalance({ address, chainId, formatUnits, token });
}
function useBalance({
  address,
  cacheTime,
  chainId: chainId_,
  enabled = true,
  formatUnits,
  scopeKey,
  staleTime,
  suspense,
  token,
  watch,
  onError,
  onSettled,
  onSuccess
} = {}) {
  const chainId = useChainId({ chainId: chainId_ });
  const queryKey_ = computed5(
    () => queryKey4({
      address,
      chainId,
      formatUnits,
      scopeKey,
      token
    })
  );
  const balanceQuery = useQuery(queryKey_, queryFn4, {
    cacheTime,
    enabled: computed5(() => !!(unref8(enabled) && unref8(address))),
    staleTime,
    suspense,
    onError,
    onSettled,
    onSuccess
  });
  useInvalidateOnBlock({
    chainId,
    enabled: computed5(
      () => !!(unref8(enabled) && unref8(watch) && unref8(address))
    ),
    queryKey: queryKey_
  });
  return balanceQuery;
}

// src/hooks/accounts/useConnect.ts
import { useMutation } from "@tanstack/vue-query";
import { connect } from "@wagmi/core";
import { computed as computed6, toRaw, unref as unref9 } from "vue-demi";
var mutationKey = (args) => [{ entity: "connect", ...args }];
var mutationFn = (args) => {
  const { chainId, connector } = args;
  if (!connector)
    throw new Error("connector is required");
  return connect({ chainId, connector });
};
function useConnect({
  chainId,
  connector,
  onError,
  onMutate,
  onSettled,
  onSuccess
} = {}) {
  const config = useConfig();
  const queryClient = useQueryClient();
  const {
    data,
    error,
    isError,
    isIdle,
    isLoading,
    isSuccess,
    mutate,
    mutateAsync,
    reset,
    status,
    variables
  } = useMutation(mutationKey({ chainId, connector }), mutationFn, {
    queryClient,
    onError,
    onMutate,
    onSettled,
    onSuccess
  });
  const connectors = computed6(() => config.value.connectors);
  const pendingConnector = computed6(() => variables?.value?.connector);
  const connect2 = (args) => {
    return mutate({
      chainId: unref9(args?.chainId) ?? unref9(chainId),
      connector: toRaw(unref9(args?.connector)) ?? toRaw(connector)
    });
  };
  const connectAsync = (args) => {
    return mutateAsync({
      chainId: unref9(args?.chainId) ?? unref9(chainId),
      connector: toRaw(unref9(args?.connector)) ?? toRaw(connector)
    });
  };
  return {
    connect: connect2,
    connectAsync,
    connectors,
    pendingConnector,
    data,
    error,
    isError,
    isIdle,
    isLoading,
    isSuccess,
    reset,
    status,
    variables
  };
}

// src/hooks/accounts/useDisconnect.ts
import { useMutation as useMutation2 } from "@tanstack/vue-query";
import { disconnect } from "@wagmi/core";
var mutationKey2 = [{ entity: "disconnect" }];
var mutationFn2 = () => disconnect();
function useDisconnect({
  onError,
  onMutate,
  onSettled,
  onSuccess
} = {}) {
  const queryClient = useQueryClient();
  const {
    error,
    isError,
    isIdle,
    isLoading,
    isSuccess,
    mutate: disconnect2,
    mutateAsync: disconnectAsync,
    reset,
    status
  } = useMutation2(mutationKey2, mutationFn2, {
    queryClient,
    ...onError ? {
      onError(error2, _variables, context) {
        onError(error2, context);
      }
    } : {},
    onMutate,
    ...onSettled ? {
      onSettled(_data, error2, _variables, context) {
        onSettled(error2, context);
      }
    } : {},
    ...onSuccess ? {
      onSuccess(_data, _variables, context) {
        onSuccess(context);
      }
    } : {}
  });
  return {
    disconnect: disconnect2,
    disconnectAsync,
    error,
    isError,
    isIdle,
    isLoading,
    isSuccess,
    reset,
    status
  };
}

// src/hooks/accounts/useNetwork.ts
import { getNetwork, watchNetwork } from "@wagmi/core";
import { getCurrentScope as getCurrentScope2, onScopeDispose as onScopeDispose2, reactive as reactive2, toRefs as toRefs2 } from "vue-demi";
function useNetwork() {
  const network = reactive2(getNetwork());
  const unwatch = watchNetwork((data) => {
    updateState(network, data);
  });
  if (getCurrentScope2())
    onScopeDispose2(() => unwatch());
  return toRefs2(network);
}

// src/hooks/accounts/useSignMessage.ts
import { useMutation as useMutation3 } from "@tanstack/vue-query";
import { signMessage } from "@wagmi/core";
import { unref as unref10 } from "vue-demi";
var mutationKey3 = (args) => [{ entity: "signMessage", ...args }];
var mutationFn3 = (args) => {
  const { message } = args;
  if (!message)
    throw new Error("message is required");
  return signMessage({ message });
};
function useSignMessage({
  message,
  onError,
  onMutate,
  onSettled,
  onSuccess
} = {}) {
  const queryClient = useQueryClient();
  const {
    data,
    error,
    isError,
    isIdle,
    isLoading,
    isSuccess,
    mutate,
    mutateAsync,
    reset,
    status,
    variables
  } = useMutation3(mutationKey3({ message }), mutationFn3, {
    queryClient,
    onError,
    onMutate,
    onSettled,
    onSuccess
  });
  const signMessage2 = (args) => {
    return mutate({
      message: unref10(args?.message) ?? unref10(message)
    });
  };
  const signMessageAsync = (args) => {
    return mutateAsync({
      message: unref10(args?.message) ?? unref10(message)
    });
  };
  return {
    data,
    error,
    isError,
    isIdle,
    isLoading,
    isSuccess,
    reset,
    signMessage: signMessage2,
    signMessageAsync,
    status,
    variables
  };
}

// src/hooks/accounts/useSignTypedData.ts
import { useMutation as useMutation4 } from "@tanstack/vue-query";
import { signTypedData } from "@wagmi/core";
function mutationKey4({ domain, types, message, primaryType }) {
  return [
    { entity: "signTypedData", domain, types, message, primaryType }
  ];
}
function mutationFn4(args) {
  const { domain, types, primaryType, message } = args;
  if (!domain)
    throw new Error("domain is required");
  if (!types)
    throw new Error("types is required");
  if (!primaryType)
    throw new Error("primaryType is required");
  if (!message)
    throw new Error("message is required");
  return signTypedData({
    domain,
    message,
    primaryType,
    types
  });
}
function useSignTypedData({
  domain,
  types,
  message,
  primaryType,
  onError,
  onMutate,
  onSettled,
  onSuccess
} = {}) {
  const queryClient = useQueryClient();
  const {
    data,
    error,
    isError,
    isIdle,
    isLoading,
    isSuccess,
    mutate,
    mutateAsync,
    reset,
    status,
    variables
  } = useMutation4(
    mutationKey4({
      domain,
      message,
      primaryType,
      types
    }),
    mutationFn4,
    {
      queryClient,
      onError,
      onMutate,
      onSettled,
      onSuccess
    }
    // TODO: type fix
  );
  const signTypedData2 = (args) => {
    const _args = cloneDeepUnref({
      domain: args?.domain ?? domain,
      types: args?.types ?? types,
      message: args?.message ?? message,
      primaryType: args?.primaryType ?? primaryType
    });
    mutate(_args);
  };
  const signTypedDataAsync = () => (args) => {
    const _args = cloneDeepUnref({
      domain: args?.domain ?? domain,
      types: args?.types ?? types,
      message: args?.message ?? message,
      primaryType: args?.primaryType ?? primaryType
    });
    mutateAsync(_args);
  };
  return {
    data,
    error,
    isError,
    isIdle,
    isLoading,
    isSuccess,
    reset,
    signTypedData: signTypedData2,
    signTypedDataAsync,
    status,
    variables
  };
}

// src/hooks/accounts/useSwitchNetwork.ts
import { useMutation as useMutation5 } from "@tanstack/vue-query";
import { switchNetwork } from "@wagmi/core";
import { computed as computed7, unref as unref11 } from "vue-demi";
var mutationKey5 = (args) => [{ entity: "switchNetwork", ...args }];
var mutationFn5 = (args) => {
  const { chainId } = args;
  if (!chainId)
    throw new Error("chainId is required");
  return switchNetwork({ chainId });
};
function useSwitchNetwork({
  chainId,
  throwForSwitchChainNotSupported,
  onError,
  onMutate,
  onSettled,
  onSuccess
} = {}) {
  const queryClient = useQueryClient();
  const config = useConfig();
  const {
    data,
    error,
    isError,
    isIdle,
    isLoading,
    isSuccess,
    mutate,
    mutateAsync,
    reset,
    status,
    variables
  } = useMutation5(mutationKey5({ chainId }), mutationFn5, {
    queryClient,
    onError,
    onMutate,
    onSettled,
    onSuccess
  });
  const chains = computed7(() => config.value.chains ?? []);
  const pendingChainId = computed7(() => variables.value?.chainId);
  const supports = computed7(
    () => unref11(throwForSwitchChainNotSupported) || !!config.value.connector?.switchChain
  );
  const switchNetwork2 = (chainId_) => mutate({ chainId: unref11(chainId_) ?? unref11(chainId) });
  const switchNetworkAsync = (chainId_) => mutateAsync({
    chainId: unref11(chainId_) ?? unref11(chainId)
  });
  return {
    chains,
    data,
    error,
    isError,
    isIdle,
    isLoading,
    isSuccess,
    pendingChainId,
    reset,
    status,
    supports,
    switchNetwork: switchNetwork2,
    switchNetworkAsync,
    variables
  };
}

// src/hooks/contracts/useContractEvent.ts
import { unref as unref12, watchEffect as watchEffect6 } from "vue-demi";
function useContractEvent({
  address,
  chainId,
  abi,
  listener,
  eventName
} = {}) {
  const publicClient = usePublicClient({ chainId });
  const webSocketPublicClient = useWebSocketPublicClient({ chainId });
  const unwatch = watchEffect6((onCleanup) => {
    if (!unref12(abi) || !unref12(address) || !unref12(eventName))
      return;
    const publicClient_ = webSocketPublicClient.value || publicClient.value;
    const _unwatch = publicClient_.watchContractEvent({
      abi: unref12(abi),
      address: unref12(address),
      eventName: unref12(eventName),
      onLogs: listener
    });
    onCleanup(() => _unwatch());
  });
  return unwatch;
}

// src/hooks/contracts/useContractInfiniteReads.ts
import { replaceEqualDeep } from "@tanstack/vue-query";
import { deepEqual, readContracts } from "@wagmi/core";
import { computed as computed8, unref as unref13 } from "vue-demi";
function queryKey5({
  allowFailure,
  blockNumber,
  blockTag,
  cacheKey,
  scopeKey
}) {
  return [
    {
      entity: "readContractsInfinite",
      allowFailure,
      blockNumber,
      blockTag,
      cacheKey,
      scopeKey
    }
  ];
}
function queryFn5({
  contracts
}) {
  return ({
    queryKey: [{ allowFailure, blockNumber, blockTag }],
    pageParam
  }) => {
    return readContracts({
      allowFailure,
      blockNumber,
      blockTag,
      contracts: contracts(pageParam || void 0)
    });
  };
}
function useContractInfiniteReads({
  allowFailure,
  blockNumber,
  blockTag,
  cacheKey,
  cacheTime,
  contracts,
  enabled: enabled_ = true,
  getNextPageParam,
  isDataEqual,
  keepPreviousData,
  onError,
  onSettled,
  onSuccess,
  scopeKey,
  select,
  staleTime,
  structuralSharing = (oldData, newData) => deepEqual(oldData, newData) ? oldData : replaceEqualDeep(oldData, newData),
  suspense
}) {
  const queryKey_ = computed8(
    () => queryKey5({ allowFailure, blockNumber, blockTag, cacheKey, scopeKey })
  );
  const enabled = computed8(() => Boolean(unref13(enabled_) && contracts));
  return useInfiniteQuery(queryKey_, queryFn5({ contracts }), {
    cacheTime,
    enabled,
    getNextPageParam,
    isDataEqual,
    keepPreviousData,
    select,
    staleTime,
    structuralSharing,
    suspense,
    onError,
    onSettled,
    onSuccess
  });
}
function paginatedIndexesConfig(fn, {
  perPage,
  start,
  direction
}) {
  const contracts = (page = 0) => [...Array(perPage).keys()].map((index) => {
    return unref13(direction) === "increment" ? unref13(start) + index + page * unref13(perPage) : unref13(start) - index - page * unref13(perPage);
  }).filter((index) => index >= 0).map(fn).flat();
  return {
    contracts,
    getNextPageParam(lastPage, pages) {
      return lastPage?.length === unref13(perPage) ? pages.length : void 0;
    }
  };
}

// src/hooks/contracts/useContractRead.ts
import { replaceEqualDeep as replaceEqualDeep2 } from "@tanstack/vue-query";
import { deepEqual as deepEqual2, readContract } from "@wagmi/core";
import { computed as computed9, unref as unref14 } from "vue-demi";
function queryKey6({
  address,
  args,
  blockNumber,
  blockTag,
  chainId,
  functionName,
  scopeKey
}) {
  return [
    {
      entity: "readContract",
      address,
      args,
      blockNumber,
      blockTag,
      chainId,
      functionName,
      scopeKey
    }
  ];
}
function queryFn6({ abi }) {
  return async ({
    queryKey: [{ address, args, blockNumber, blockTag, chainId, functionName }]
  }) => {
    if (!abi)
      throw new Error("abi is required");
    if (!address)
      throw new Error("address is required");
    return await readContract({
      address,
      args,
      blockNumber,
      blockTag,
      chainId,
      // TODO: Remove cast and still support `Narrow<TAbi>`
      abi,
      functionName
    }) ?? null;
  };
}
function useContractRead({
  abi,
  address,
  args,
  blockNumber: blockNumberOverride,
  blockTag,
  cacheOnBlock = false,
  cacheTime,
  chainId: chainId_,
  enabled: enabled_ = true,
  functionName,
  isDataEqual,
  onError,
  onSettled,
  onSuccess,
  scopeKey,
  select,
  staleTime,
  structuralSharing = (oldData, newData) => deepEqual2(oldData, newData) ? oldData : replaceEqualDeep2(oldData, newData),
  suspense,
  watch
} = {}) {
  const chainId = useChainId({ chainId: chainId_ });
  const { data: blockNumber_ } = useBlockNumber({
    chainId,
    enabled: computed9(() => unref14(watch) || unref14(cacheOnBlock)),
    scopeKey: computed9(
      () => unref14(watch) || unref14(cacheOnBlock) ? void 0 : "idle"
    ),
    watch
  });
  const blockNumber = computed9(
    () => unref14(blockNumberOverride) ?? blockNumber_.value
  );
  const queryKey_ = computed9(
    () => queryKey6({
      address,
      args,
      blockNumber: computed9(
        () => unref14(cacheOnBlock) ? blockNumber.value : void 0
      ),
      blockTag,
      chainId,
      functionName,
      scopeKey
    })
  );
  const enabled = computed9(() => {
    let enabled2 = Boolean(
      unref14(enabled_) && unref14(abi) && unref14(address) && unref14(functionName)
    );
    if (unref14(cacheOnBlock))
      enabled2 = Boolean(unref14(enabled2) && unref14(blockNumber));
    return enabled2;
  });
  useInvalidateOnBlock({
    chainId,
    enabled: computed9(
      () => Boolean(unref14(enabled) && unref14(watch) && !unref14(cacheOnBlock))
    ),
    queryKey: unref14(queryKey_)
  });
  return useQuery(
    queryKey_,
    queryFn6({
      // TODO: Remove cast and still support `Narrow<TAbi>`
      abi: unref14(abi)
    }),
    {
      cacheTime,
      enabled,
      isDataEqual,
      select,
      staleTime,
      structuralSharing,
      suspense,
      onError,
      onSettled,
      onSuccess
    }
  );
}

// src/hooks/contracts/useContractReads.ts
import { replaceEqualDeep as replaceEqualDeep3 } from "@tanstack/vue-query";
import { deepEqual as deepEqual3, readContracts as readContracts2 } from "@wagmi/core";
import { computed as computed10, unref as unref15 } from "vue-demi";
function queryKey7({
  allowFailure,
  blockNumber,
  blockTag,
  chainId,
  contracts,
  scopeKey
}) {
  return [
    {
      entity: "readContracts",
      allowFailure,
      blockNumber,
      blockTag,
      chainId,
      scopeKey,
      contracts: (unref15(contracts) ?? []).map(({ address, args, chainId: chainId2, functionName }) => ({
        address,
        args,
        chainId: chainId2,
        functionName
      }))
    }
  ];
}
function queryFn7({ abis }) {
  return ({
    queryKey: [{ allowFailure, blockNumber, blockTag, contracts: contracts_ }]
  }) => {
    const contracts = contracts_.map((contract, i) => ({
      ...contract,
      abi: unref15(abis)[i]
    }));
    return readContracts2({
      allowFailure,
      contracts,
      blockNumber,
      blockTag
    });
  };
}
function useContractReads({
  allowFailure: allowFailure_,
  blockNumber: blockNumberOverride,
  blockTag,
  cacheOnBlock = false,
  cacheTime,
  contracts,
  enabled: enabled_ = true,
  isDataEqual,
  keepPreviousData,
  onError,
  onSettled,
  onSuccess,
  scopeKey,
  select,
  staleTime,
  structuralSharing = (oldData, newData) => deepEqual3(oldData, newData) ? oldData : replaceEqualDeep3(oldData, newData),
  suspense,
  watch
} = {}) {
  const allowFailure = computed10(() => unref15(allowFailure_) ?? true);
  const { data: blockNumber_ } = useBlockNumber({
    enabled: computed10(() => unref15(watch) && unref15(cacheOnBlock)),
    watch
  });
  const chainId = useChainId();
  const blockNumber = computed10(
    () => unref15(blockNumberOverride) ?? blockNumber_.value
  );
  const queryKey_ = computed10(
    () => queryKey7({
      allowFailure,
      blockNumber: cacheOnBlock ? blockNumber : void 0,
      blockTag,
      chainId,
      contracts,
      scopeKey
    })
  );
  const enabled = computed10(() => {
    let enabled2 = Boolean(
      unref15(enabled_) && unref15(contracts)?.every(
        (x) => x.abi && x.address && x.functionName
      )
    );
    if (unref15(cacheOnBlock))
      enabled2 = Boolean(unref15(enabled2) && unref15(blockNumber));
    return enabled2;
  });
  useInvalidateOnBlock({
    enabled: Boolean(unref15(enabled) && unref15(watch) && !unref15(cacheOnBlock)),
    queryKey: queryKey_.value
  });
  const abis = computed10(() => (unref15(contracts) ?? []).map(({ abi }) => abi));
  return useQuery(queryKey_, queryFn7({ abis }), {
    cacheTime,
    enabled,
    isDataEqual,
    keepPreviousData,
    staleTime,
    select,
    structuralSharing,
    suspense,
    onError,
    onSettled,
    onSuccess
  });
}

// src/hooks/contracts/useContractWrite.ts
import { useMutation as useMutation6 } from "@tanstack/vue-query";
import { writeContract } from "@wagmi/core";
import { getSendTransactionParameters } from "@wagmi/core/internal";
import { unref as unref16 } from "vue-demi";
function mutationKey6({
  address,
  abi,
  functionName,
  ...config
}) {
  const {
    args,
    accessList,
    account,
    dataSuffix,
    gas,
    gasPrice,
    maxFeePerGas,
    maxPriorityFeePerGas,
    nonce,
    request,
    value
  } = config;
  return [
    {
      entity: "writeContract",
      address,
      args,
      abi,
      accessList,
      account,
      dataSuffix,
      functionName,
      gas,
      gasPrice,
      maxFeePerGas,
      maxPriorityFeePerGas,
      nonce,
      request,
      value
    }
  ];
}
function mutationFn6(config) {
  if (config.mode === "prepared") {
    if (!config.request)
      throw new Error("request is required");
    return writeContract({
      mode: "prepared",
      request: config.request
    });
  }
  if (!config.address)
    throw new Error("address is required");
  if (!config.abi)
    throw new Error("abi is required");
  if (!config.functionName)
    throw new Error("functionName is required");
  return writeContract({
    address: config.address,
    args: config.args,
    chainId: config.chainId,
    abi: config.abi,
    // TODO: Remove cast and still support `Narrow<TAbi>`
    functionName: config.functionName,
    accessList: config.accessList,
    account: config.account,
    dataSuffix: config.dataSuffix,
    gas: config.gas,
    gasPrice: config.gasPrice,
    maxFeePerGas: config.maxFeePerGas,
    maxPriorityFeePerGas: config.maxPriorityFeePerGas,
    nonce: config.nonce,
    value: config.value
  });
}
function useContractWrite(config) {
  const { address, abi, args, chainId, functionName, mode, request, dataSuffix } = config;
  const _config = cloneDeepUnref(config);
  const {
    accessList,
    account,
    gas,
    gasPrice,
    maxFeePerGas,
    maxPriorityFeePerGas,
    nonce,
    value
  } = getSendTransactionParameters(
    _config
  );
  const {
    data,
    error,
    isError,
    isIdle,
    isLoading,
    isSuccess,
    mutate,
    mutateAsync,
    reset,
    status,
    variables
  } = useMutation6(
    mutationKey6({
      address,
      abi,
      functionName,
      chainId,
      mode,
      args,
      accessList,
      account,
      dataSuffix,
      gas,
      gasPrice,
      maxFeePerGas,
      maxPriorityFeePerGas,
      nonce,
      request,
      value
    }),
    mutationFn6,
    {
      onError: config.onError,
      onMutate: config.onMutate,
      onSettled: config.onSettled,
      onSuccess: config.onSuccess
    }
  );
  const write = (overrideConfig) => {
    if (unref16(mode) === "prepared") {
      if (!unref16(request))
        return void 0;
      const mutateKey2 = cloneDeepUnref({
        mode: "prepared",
        request: config.request,
        chainId: config.chainId
      });
      return mutate(mutateKey2);
    }
    const mutateKey = cloneDeepUnref({
      address,
      args,
      abi,
      functionName,
      chainId,
      accessList,
      account,
      dataSuffix,
      gas,
      gasPrice,
      maxFeePerGas,
      maxPriorityFeePerGas,
      nonce,
      value,
      ...overrideConfig
    });
    return mutate(mutateKey);
  };
  const writeAsync = (overrideConfig) => {
    if (unref16(mode) === "prepared") {
      if (!unref16(request))
        return void 0;
      const mutateKey2 = cloneDeepUnref({
        mode: "prepared",
        request: config.request
      });
      return mutateAsync(mutateKey2);
    }
    const mutateKey = cloneDeepUnref({
      address,
      args,
      abi,
      chainId,
      functionName,
      accessList,
      account,
      gas,
      gasPrice,
      maxFeePerGas,
      maxPriorityFeePerGas,
      nonce,
      value,
      ...overrideConfig
    });
    return mutateAsync(mutateKey);
  };
  return {
    data,
    error,
    isError,
    isIdle,
    isLoading,
    isSuccess,
    reset,
    status,
    variables,
    write,
    writeAsync
  };
}

// src/hooks/contracts/usePrepareContractWrite.ts
import { prepareWriteContract } from "@wagmi/core";
import { getCallParameters } from "@wagmi/core/internal";
import { computed as computed11, reactive as reactive3, toRefs as toRefs3, unref as unref17, watchEffect as watchEffect7 } from "vue-demi";
function queryKey8({
  accessList,
  account,
  activeChainId,
  args,
  address,
  blockNumber,
  blockTag,
  chainId,
  functionName,
  gas,
  gasPrice,
  maxFeePerGas,
  maxPriorityFeePerGas,
  nonce,
  scopeKey,
  walletClientAddress,
  value
}) {
  return [
    {
      entity: "prepareContractTransaction",
      accessList,
      account,
      activeChainId,
      address,
      args,
      blockNumber,
      blockTag,
      chainId,
      functionName,
      gas,
      gasPrice,
      maxFeePerGas,
      maxPriorityFeePerGas,
      nonce,
      scopeKey,
      walletClientAddress,
      value
    }
  ];
}
function queryFn8({
  abi,
  walletClient
}) {
  return ({
    queryKey: [
      {
        accessList,
        account,
        args,
        address,
        blockNumber,
        blockTag,
        chainId,
        functionName,
        gas,
        gasPrice,
        maxFeePerGas,
        maxPriorityFeePerGas,
        nonce,
        value
      }
    ]
  }) => {
    if (!abi)
      throw new Error("abi is required");
    if (!address)
      throw new Error("address is required");
    if (!functionName)
      throw new Error("functionName is required");
    return prepareWriteContract({
      // TODO: Remove cast and still support `Narrow<TAbi>`
      abi,
      accessList,
      account,
      args,
      address,
      blockNumber,
      blockTag,
      chainId,
      functionName,
      gas,
      gasPrice,
      maxFeePerGas,
      maxPriorityFeePerGas,
      nonce,
      walletClient,
      value
    });
  };
}
function usePrepareContractWrite({
  address,
  abi,
  functionName,
  chainId,
  args,
  cacheTime,
  enabled = true,
  scopeKey,
  staleTime,
  suspense,
  onError,
  onSettled,
  onSuccess,
  ...config
} = {}) {
  const { chain: activeChain } = useNetwork();
  const { data: walletClient } = useWalletClient({ chainId });
  const _config = cloneDeepUnref(config);
  const {
    accessList,
    account,
    blockNumber,
    blockTag,
    gas,
    gasPrice,
    maxFeePerGas,
    maxPriorityFeePerGas,
    nonce,
    value
  } = getCallParameters(_config);
  const prepareContractWriteQuery = useQuery(
    queryKey8({
      accessList,
      account,
      activeChainId: activeChain?.value?.id,
      address,
      args,
      blockNumber,
      blockTag,
      chainId,
      functionName,
      gas,
      gasPrice,
      maxFeePerGas,
      maxPriorityFeePerGas,
      nonce,
      scopeKey,
      walletClientAddress: walletClient.value?.account.address,
      value
    }),
    queryFn8({
      // TODO: Remove cast and still support `Narrow<TAbi>`
      abi: unref17(abi),
      walletClient: unref17(walletClient)
    }),
    {
      cacheTime,
      enabled: computed11(
        () => !!(unref17(enabled) && unref17(abi) && unref17(address) && unref17(functionName) && unref17(walletClient))
      ),
      staleTime,
      suspense,
      onError,
      onSettled,
      onSuccess
    }
  );
  const data = reactive3({
    chainId,
    mode: "prepared",
    request: void 0,
    result: void 0
  });
  watchEffect7(() => {
    updateState(data, prepareContractWriteQuery.data.value || {});
  });
  return Object.assign(prepareContractWriteQuery, {
    config: {
      ...toRefs3(data)
    }
    // TODO: fix type
  });
}

// src/hooks/contracts/useToken.ts
import { fetchToken } from "@wagmi/core";
import { computed as computed12, unref as unref18 } from "vue-demi";
function queryKey9({
  address,
  chainId,
  formatUnits,
  scopeKey
}) {
  return [{ entity: "token", address, chainId, formatUnits, scopeKey }];
}
function queryFn9({
  queryKey: [{ address, chainId, formatUnits }]
}) {
  if (!address)
    throw new Error("address is required");
  return fetchToken({ address, chainId, formatUnits });
}
function useToken({
  address,
  chainId: chainId_,
  formatUnits = "ether",
  cacheTime,
  enabled = true,
  scopeKey,
  staleTime = 1e3 * 60 * 60 * 24,
  // 24 hours
  suspense,
  onError,
  onSettled,
  onSuccess
} = {}) {
  const chainId = useChainId({ chainId: chainId_ });
  return useQuery(
    queryKey9({ address, chainId, formatUnits, scopeKey }),
    queryFn9,
    {
      cacheTime,
      enabled: computed12(() => unref18(enabled) && !!unref18(address)),
      staleTime,
      suspense,
      onError,
      onSettled,
      onSuccess
    }
  );
}

// src/hooks/ens/useEnsAddress.ts
import { fetchEnsAddress } from "@wagmi/core";
import { computed as computed13, unref as unref19 } from "vue-demi";
function queryKey10({ chainId, name, scopeKey }) {
  return [{ entity: "ensAddress", chainId, name, scopeKey }];
}
function queryFn10({
  queryKey: [{ chainId, name }]
}) {
  if (!name)
    throw new Error("name is required");
  return fetchEnsAddress({ chainId, name });
}
function useEnsAddress({
  cacheTime,
  chainId: chainId_,
  enabled = true,
  name,
  scopeKey,
  staleTime = 1e3 * 60 * 60 * 24,
  // 24 hours
  suspense,
  onError,
  onSettled,
  onSuccess
} = {}) {
  const chainId = useChainId({ chainId: chainId_ });
  return useQuery(queryKey10({ chainId, name, scopeKey }), queryFn10, {
    cacheTime,
    enabled: computed13(
      () => !!(unref19(enabled) && unref19(chainId) && unref19(name))
    ),
    staleTime,
    suspense,
    onError,
    onSettled,
    onSuccess
  });
}

// src/hooks/ens/useEnsAvatar.ts
import { fetchEnsAvatar } from "@wagmi/core";
import { computed as computed14, unref as unref20 } from "vue-demi";
function queryKey11({ name, chainId, scopeKey }) {
  return [{ entity: "ensAvatar", name, chainId, scopeKey }];
}
function queryFn11({
  queryKey: [{ name, chainId }]
}) {
  if (!name)
    throw new Error("name is required");
  return fetchEnsAvatar({ name, chainId });
}
function useEnsAvatar({
  cacheTime,
  chainId: chainId_,
  enabled = true,
  name,
  scopeKey,
  staleTime = 1e3 * 60 * 60 * 24,
  // 24 hours
  suspense,
  onError,
  onSettled,
  onSuccess
} = {}) {
  const chainId = useChainId({ chainId: chainId_ });
  return useQuery(queryKey11({ name, chainId, scopeKey }), queryFn11, {
    cacheTime,
    enabled: computed14(
      () => !!(unref20(enabled) && unref20(name) && unref20(chainId))
    ),
    staleTime,
    suspense,
    onError,
    onSettled,
    onSuccess
  });
}

// src/hooks/ens/useEnsName.ts
import { fetchEnsName } from "@wagmi/core";
import { computed as computed15, unref as unref21 } from "vue-demi";
function queryKey12({
  address,
  chainId,
  scopeKey
}) {
  return [{ entity: "ensName", address, chainId, scopeKey }];
}
function queryFn12({
  queryKey: [{ address, chainId }]
}) {
  if (!address)
    throw new Error("address is required");
  return fetchEnsName({ address, chainId });
}
function useEnsName({
  address,
  cacheTime,
  chainId: chainId_,
  enabled = true,
  scopeKey,
  staleTime = 1e3 * 60 * 60 * 24,
  // 24 hours
  suspense,
  onError,
  onSettled,
  onSuccess
} = {}) {
  const chainId = useChainId({ chainId: chainId_ });
  return useQuery(queryKey12({ address, chainId, scopeKey }), queryFn12, {
    cacheTime,
    enabled: computed15(
      () => !!(unref21(enabled) && unref21(address) && unref21(chainId))
    ),
    staleTime,
    suspense,
    onError,
    onSettled,
    onSuccess
  });
}

// src/hooks/ens/useEnsResolver.ts
import { fetchEnsResolver } from "@wagmi/core";
import { computed as computed16, unref as unref22 } from "vue-demi";
function queryKey13({ chainId, name, scopeKey }) {
  return [
    { entity: "ensResolver", chainId, name, scopeKey, persist: false }
  ];
}
function queryFn13({
  queryKey: [{ chainId, name }]
}) {
  if (!name)
    throw new Error("name is required");
  return fetchEnsResolver({ chainId, name });
}
function useEnsResolver({
  chainId: chainId_,
  name,
  enabled = true,
  scopeKey,
  suspense,
  onError,
  onSettled,
  onSuccess
} = {}) {
  const chainId = useChainId({ chainId: chainId_ });
  return useQuery(queryKey13({ chainId, name, scopeKey }), queryFn13, {
    cacheTime: 0,
    enabled: computed16(
      () => !!(unref22(enabled) && unref22(chainId) && unref22(name))
    ),
    suspense,
    onError,
    onSettled,
    onSuccess
  });
}

// src/hooks/transactions/usePrepareSendTransaction.ts
import { prepareSendTransaction } from "@wagmi/core";
import { computed as computed17, reactive as reactive4, toRefs as toRefs4, unref as unref23, watchEffect as watchEffect8 } from "vue-demi";
function queryKey14({
  accessList,
  account,
  activeChainId,
  chainId,
  data,
  gas,
  gasPrice,
  maxFeePerGas,
  maxPriorityFeePerGas,
  nonce,
  to,
  value,
  scopeKey,
  walletClientAddress
}) {
  return [
    {
      entity: "prepareSendTransaction",
      activeChainId,
      accessList,
      account,
      chainId,
      data,
      gas,
      gasPrice,
      maxFeePerGas,
      maxPriorityFeePerGas,
      nonce,
      to,
      value,
      scopeKey,
      walletClientAddress
    }
  ];
}
function queryFn14({ walletClient }) {
  return ({
    queryKey: [
      {
        accessList,
        account,
        chainId,
        data,
        gas,
        gasPrice,
        maxFeePerGas,
        maxPriorityFeePerGas,
        nonce,
        to,
        value
      }
    ]
  }) => {
    if (!to)
      throw new Error("to is required");
    return prepareSendTransaction({
      accessList,
      account,
      chainId,
      data,
      gas,
      gasPrice,
      maxFeePerGas,
      maxPriorityFeePerGas,
      nonce,
      to,
      value,
      walletClient
    });
  };
}
function usePrepareSendTransaction({
  accessList,
  account,
  chainId,
  cacheTime,
  data,
  enabled = true,
  gas,
  gasPrice,
  maxFeePerGas,
  maxPriorityFeePerGas,
  nonce,
  scopeKey,
  staleTime,
  suspense,
  to,
  value,
  onError,
  onSettled,
  onSuccess
} = {}) {
  const { chain: activeChain } = useNetwork();
  const { data: walletClient } = useWalletClient({ chainId });
  const prepareSendTransactionQuery = useQuery(
    queryKey14({
      accessList,
      activeChainId: activeChain?.value?.id,
      account,
      chainId,
      data,
      gas,
      gasPrice,
      maxFeePerGas,
      maxPriorityFeePerGas,
      nonce,
      scopeKey,
      to,
      value,
      walletClientAddress: walletClient.value?.account.address
    }),
    queryFn14({ walletClient: walletClient.value }),
    {
      cacheTime,
      enabled: computed17(
        () => !!(unref23(enabled) && unref23(walletClient) && unref23(to))
      ),
      staleTime,
      suspense,
      onError,
      onSettled,
      onSuccess
    }
  );
  const config = reactive4({
    mode: "prepared",
    accessList: void 0,
    account: void 0,
    chainId: void 0,
    data: void 0,
    gas: void 0,
    gasPrice: void 0,
    maxFeePerGas: void 0,
    maxPriorityFeePerGas: void 0,
    nonce: void 0,
    to: void 0,
    value: void 0
  });
  watchEffect8(() => {
    updateState(config, prepareSendTransactionQuery.data.value || {});
  });
  return Object.assign(prepareSendTransactionQuery, {
    config: { ...toRefs4(config) }
  });
}

// src/hooks/transactions/useSendTransaction.ts
import { useMutation as useMutation7 } from "@tanstack/vue-query";
import { sendTransaction } from "@wagmi/core";
import { computed as computed18, unref as unref24 } from "vue-demi";
var mutationKey7 = (args) => [{ entity: "sendTransaction", ...args }];
var mutationFn7 = ({
  accessList,
  account,
  chainId,
  data,
  gas,
  gasPrice,
  maxFeePerGas,
  maxPriorityFeePerGas,
  mode,
  nonce,
  to,
  value
}) => {
  if (!to)
    throw new Error("to is required.");
  return sendTransaction({
    accessList,
    account,
    chainId,
    data,
    gas,
    gasPrice,
    maxFeePerGas,
    maxPriorityFeePerGas,
    mode,
    nonce,
    to,
    value
  });
};
function useSendTransaction({
  accessList,
  account,
  chainId,
  data: data_,
  gas,
  gasPrice,
  maxFeePerGas,
  maxPriorityFeePerGas,
  mode,
  nonce,
  to,
  value,
  onError,
  onMutate,
  onSettled,
  onSuccess
} = {}) {
  const queryClient = useQueryClient();
  const {
    data,
    error,
    isError,
    isIdle,
    isLoading,
    isSuccess,
    mutate,
    mutateAsync,
    reset,
    status,
    variables
  } = useMutation7(
    mutationKey7({
      accessList,
      account,
      chainId,
      data: data_,
      gas,
      gasPrice,
      maxFeePerGas,
      maxPriorityFeePerGas,
      mode,
      nonce,
      to,
      value
    }),
    mutationFn7,
    {
      queryClient,
      onError,
      onMutate,
      onSettled,
      onSuccess
    }
  );
  const disabled = computed18(() => unref24(mode) === "prepared" && !unref24(to));
  const sendTransaction2 = (args) => {
    const _args = cloneDeepUnref({
      chainId,
      mode,
      ...args || {
        accessList,
        account,
        chainId,
        data: data_,
        gas,
        gasPrice,
        maxFeePerGas,
        maxPriorityFeePerGas,
        mode,
        nonce,
        value,
        to
      }
    });
    return mutate(_args);
  };
  const sendTransactionAsync = (args) => {
    const _args = cloneDeepUnref({
      chainId,
      mode,
      ...args || {
        accessList,
        account,
        chainId,
        data: data_,
        gas,
        gasPrice,
        maxFeePerGas,
        maxPriorityFeePerGas,
        mode,
        nonce,
        value,
        to
      }
    });
    return mutateAsync(_args);
  };
  return {
    data,
    error,
    isError,
    isIdle,
    isLoading,
    isSuccess,
    reset,
    disabled,
    sendTransaction: sendTransaction2,
    sendTransactionAsync,
    status,
    variables
  };
}

// src/hooks/transactions/useTransaction.ts
import { fetchTransaction } from "@wagmi/core";
import { computed as computed19, unref as unref25 } from "vue-demi";
function queryKey15({ chainId, hash, scopeKey }) {
  return [{ entity: "transaction", chainId, hash, scopeKey }];
}
function queryFn15({
  queryKey: [{ chainId, hash }]
}) {
  if (!hash)
    throw new Error("hash is required");
  return fetchTransaction({ chainId, hash });
}
function useTransaction({
  cacheTime = 0,
  chainId: chainId_,
  enabled = true,
  hash,
  scopeKey,
  staleTime,
  suspense,
  onError,
  onSettled,
  onSuccess
} = {}) {
  const chainId = useChainId({ chainId: chainId_ });
  return useQuery(queryKey15({ chainId, hash, scopeKey }), queryFn15, {
    cacheTime,
    enabled: computed19(() => !!(unref25(enabled) && unref25(hash))),
    staleTime,
    suspense,
    onError,
    onSettled,
    onSuccess
  });
}

// src/hooks/transactions/useWaitForTransaction.ts
import { waitForTransaction } from "@wagmi/core";
import { computed as computed20, unref as unref26 } from "vue-demi";
function queryKey16({
  confirmations,
  chainId,
  hash,
  scopeKey,
  timeout
}) {
  return [
    {
      entity: "waitForTransaction",
      confirmations,
      chainId,
      hash,
      scopeKey,
      timeout
    }
  ];
}
function queryFn16({
  onReplaced
}) {
  return ({
    queryKey: [{ chainId, confirmations, hash: hash_, timeout }]
  }) => {
    const hash = unref26(hash_);
    if (!hash)
      throw new Error("hash is required");
    return waitForTransaction({
      chainId,
      confirmations,
      hash,
      onReplaced,
      timeout
    });
  };
}
function useWaitForTransaction({
  chainId: chainId_,
  confirmations,
  hash,
  timeout,
  cacheTime,
  enabled = true,
  scopeKey,
  staleTime,
  suspense,
  onError,
  onReplaced,
  onSettled,
  onSuccess
} = {}) {
  const chainId = useChainId({ chainId: chainId_ });
  return useQuery(
    queryKey16({ chainId, confirmations, hash, scopeKey, timeout }),
    queryFn16({ onReplaced }),
    {
      cacheTime,
      enabled: computed20(() => !!(unref26(enabled) && unref26(hash))),
      staleTime,
      suspense,
      onError,
      onSettled,
      onSuccess
    }
  );
}

// src/hooks/transactions/useWatchPendingTransactions.ts
import { unref as unref27, watchEffect as watchEffect9 } from "vue-demi";
function useWatchPendingTransactions({
  chainId: chainId_,
  enabled = true,
  listener
}) {
  const chainId = useChainId({ chainId: chainId_ });
  const publicClient = usePublicClient({ chainId });
  const webSocketPublicClient = useWebSocketPublicClient({ chainId });
  watchEffect9((onCleanup) => {
    if (!unref27(enabled))
      return;
    const publicClient_ = webSocketPublicClient.value ?? publicClient.value;
    const unwatch = publicClient_.watchPendingTransactions({
      onTransactions: listener
    });
    onCleanup(() => unwatch());
  });
}

// src/index.ts
import {
  ChainMismatchError,
  ChainNotConfiguredError,
  Connector,
  ConnectorAlreadyConnectedError,
  ConnectorNotFoundError,
  ConfigChainsNotFound,
  SwitchChainNotSupportedError,
  configureChains,
  createStorage as createStorage2,
  deepEqual as deepEqual4,
  deserialize,
  erc20ABI,
  erc721ABI,
  erc4626ABI,
  mainnet,
  readContracts as readContracts3,
  sepolia,
  serialize
} from "@wagmi/core";
export {
  ChainMismatchError,
  ChainNotConfiguredError,
  ConfigChainsNotFound,
  Connector,
  ConnectorAlreadyConnectedError,
  ConnectorNotFoundError,
  SwitchChainNotSupportedError,
  UseWagmiPlugin,
  configureChains,
  createConfig,
  createStorage2 as createStorage,
  deepEqual4 as deepEqual,
  deserialize,
  erc20ABI,
  erc4626ABI,
  erc721ABI,
  mainnet,
  paginatedIndexesConfig,
  readContracts3 as readContracts,
  sepolia,
  serialize,
  useAccount,
  useBalance,
  useBlockNumber,
  useChainId,
  useConfig,
  useConnect,
  useContractEvent,
  useContractInfiniteReads,
  useContractRead,
  useContractReads,
  useContractWrite,
  useDisconnect,
  useEnsAddress,
  useEnsAvatar,
  useEnsName,
  useEnsResolver,
  useFeeData,
  useInfiniteQuery,
  useNetwork,
  usePrepareContractWrite,
  usePrepareSendTransaction,
  usePublicClient,
  useQuery,
  useQueryClient,
  useSendTransaction,
  useSignMessage,
  useSignTypedData,
  useSwitchNetwork,
  useToken,
  useTransaction,
  useWaitForTransaction,
  useWalletClient,
  useWatchPendingTransactions,
  useWebSocketPublicClient
};
